import type { User } from "@auth0/auth0-react";

const AUTH0_CLAIM_NAMESPACE = "https://pandas.io" as const;

const DOMAIN_CONNECTION_MAP = {
	"preprod.carrefour-app.comparecycle.com": "Carrefour",
	"carrefour-app.comparecycle.com": "Carrefour",
} as const;

type VisionAuthClaim = "location_id" | "partner_code" | "external_logout_url";
type NamespacedVisionAuthClaim = `${typeof AUTH0_CLAIM_NAMESPACE}/${VisionAuthClaim}`;
type VisionAuthUser = User &
	Partial<Record<VisionAuthClaim, unknown>> &
	Partial<Record<NamespacedVisionAuthClaim, unknown>>;
type AuthLocation = Pick<Location, "hostname" | "origin" | "pathname" | "search" | "hash">;

function normalizeHost(host: string): string {
	return host.split(":")[0]?.toLowerCase() ?? "";
}

function getStringClaim(user: User | null | undefined, claim: VisionAuthClaim): string | undefined {
	if (!user) return undefined;

	const authUser = user as VisionAuthUser;
	const directValue = authUser[claim];
	if (typeof directValue === "string" && directValue.length > 0) return directValue;

	const namespacedClaim = `${AUTH0_CLAIM_NAMESPACE}/${claim}` as const;
	const namespacedValue = authUser[namespacedClaim];
	if (typeof namespacedValue === "string" && namespacedValue.length > 0) {
		return namespacedValue;
	}

	const appMetadata = (authUser as Record<string, unknown>).platform_app_metadata;
	if (appMetadata && typeof appMetadata === "object") {
		const metadataValue = (appMetadata as Record<string, unknown>)[claim];
		if (typeof metadataValue === "string" && metadataValue.length > 0) {
			return metadataValue;
		}
	}

	return undefined;
}

export function getConnectionForHost(host: string): string | undefined {
	return DOMAIN_CONNECTION_MAP[normalizeHost(host) as keyof typeof DOMAIN_CONNECTION_MAP];
}

export function getAuth0AuthorizationParams(location: Pick<AuthLocation, "hostname" | "origin">, audience?: string) {
	const connection = getConnectionForHost(location.hostname);

	return {
		redirect_uri: location.origin,
		...(audience ? { audience } : {}),
		...(connection ? { connection } : {}),
	};
}

export function getAuth0LoginOptions(location: Pick<AuthLocation, "pathname" | "search" | "hash">) {
	return {
		appState: {
			returnTo: `${location.pathname}${location.search}${location.hash}`,
		},
	};
}

export function getAuth0CallbackErrorRedirectUrl(
	location: Pick<AuthLocation, "pathname" | "search">,
): string | undefined {
	const searchParams = new URLSearchParams(location.search);
	const code = searchParams.get("error");

	if (!code || location.pathname === "/error" || location.pathname.endsWith("/error")) {
		return undefined;
	}

	const redirectParams = new URLSearchParams({ code });
	const errorDescription = searchParams.get("error_description");
	if (errorDescription) {
		redirectParams.set("error_description", errorDescription);
	}

	return `/error?${redirectParams.toString()}`;
}

export function getAuth0UserLocationId(user: User | null | undefined): string | undefined {
	return getStringClaim(user, "location_id");
}

export function getAuth0UserPartnerCode(user: User | null | undefined): string | undefined {
	return getStringClaim(user, "partner_code");
}

export function getAuth0UserExternalLogoutUrl(user: User | null | undefined): string | undefined {
	return getStringClaim(user, "external_logout_url");
}
